import { Injectable } from '@angular/core';
import { dump, ExifDict, ExifIFD, ImageIFD, insert, load } from 'piexifjs';
import { IMAGE_QUALITY } from '../model/image.model';
import { ProcessedFile } from '../helpers/file.helper';

@Injectable()
export class ImageProcessingService {
  #threadsCount = navigator?.hardwareConcurrency || 4;
  #isWebWorkerSupported = typeof Worker !== 'undefined';
  #currentIndex = 0;
  #webWorkersTeam: Worker[] = [];
  #webWorkersTeamDoneStatus: boolean[] = [];
  #images: ProcessedFile[] = [];
  #canvas = new OffscreenCanvas(0, 0);
  #context = this.#canvas.getContext('2d');
  readonly #imageQuality = IMAGE_QUALITY;

  getScaleDownImages(files: FileList | File[]): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      this.#images = [];
      this.#currentIndex = 0;
      if (files.length === 0) {
        resolve(this.#images);
      } else {
        if (this.#isWebWorkerSupported) {
          this.#adjustWebWorkersTeam(files.length);
          this.#webWorkersTeam.forEach((worker, index) => {
            const nextFile = this.#getNextFile(files);
            if (nextFile) {
              worker.postMessage({ cmd: 'getScaleDownImage', file: nextFile });
              worker.onmessage = async ({ data }) => {
                if (data.cmd === 'done') {
                  let blob: Blob = data.blob;
                  if (data.originImageBase64) {
                    let dstBase64: string;
                    try {
                      const exifImg: ExifDict = load(data.originImageBase64);
                      if (exifImg) {
                        if (exifImg['0th']) {
                          exifImg['0th'][ImageIFD.Orientation] = 0;
                        }
                        if (exifImg.Exif && exifImg.Exif[ExifIFD.SceneType]) {
                          exifImg.Exif[ExifIFD.SceneType] = 'Directly photographed';
                        }
                      }
                      dstBase64 = insert(dump(exifImg), data.scaleDownBase64);
                    } catch (e) {
                      dstBase64 = data.scaleDownBase64;
                      console.warn(e);
                    }
                    const base64Response = await fetch(dstBase64);
                    blob = await base64Response.blob();
                  }
                  this.#images.push({ name: data.name, blob });
                  const file = this.#getNextFile(files);
                  if (file) {
                    worker.postMessage({ cmd: 'getScaleDownImage', file });
                  } else {
                    this.#webWorkersTeamDoneStatus[index] = true;
                  }
                  if (this.#webWorkersTeamDoneStatus.every((item) => item)) {
                    this.#webWorkersTeam.forEach((w) => w.terminate());
                    this.#webWorkersTeam.length = 0;
                    resolve(this.#images);
                  }
                }
              };
            }
          });
        } else {
          await this.#getScaleDownImage(files, 0);
          resolve(this.#images);
        }
      }
    });
  }

  #getNextFile(files: FileList | File[]): File {
    if (this.#currentIndex <= files.length - 1) {
      return files[this.#currentIndex++];
    }
  }

  async #getScaleDownImage(files: FileList | File[], index: number) {
    const file = files[index];
    const imageBitmap = await createImageBitmap(file);
    const widthImage = imageBitmap.width > imageBitmap.height ? 1024 : 768;
    const heightImage = Math.floor((widthImage / imageBitmap.width) * imageBitmap.height);
    let blob: Blob;
    if (imageBitmap.width <= widthImage) {
      blob = new Blob([await file.arrayBuffer()], { type: file.type });
    } else {
      this.#canvas.width = widthImage;
      this.#canvas.height = heightImage;
      this.#context.drawImage(imageBitmap, 0, 0, widthImage, heightImage);
      const scaleDownImageBlob = await this.#canvas.convertToBlob({ quality: this.#imageQuality, type: file.type });
      const originImageBase64 = await this.#blobToBase64(file);
      const scaleDownBase64 = await this.#blobToBase64(scaleDownImageBlob);
      const exifImg: ExifDict = load(originImageBase64);
      const dstBase64 = insert(dump(exifImg), scaleDownBase64);
      const base64Response = await fetch(dstBase64);
      blob = await base64Response.blob();
      this.#context.clearRect(0, 0, widthImage, heightImage);
    }

    this.#images.push({ name: file.name, blob });

    imageBitmap.close();
    if (index < files.length - 1) {
      await this.#getScaleDownImage(files, ++index);
    }
  }

  #blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, _) => {
      let reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
        reader.onloadend = null;
        reader = null;
      };
      reader.readAsDataURL(blob);
    });
  }

  #adjustWebWorkersTeam(filesCount: number) {
    const teamSize = Math.min(filesCount, this.#threadsCount);
    if (this.#webWorkersTeam.length === 0 || this.#webWorkersTeam.length < teamSize) {
      this.#webWorkersTeam.push(
        ...new Array(Math.abs(teamSize - this.#webWorkersTeam.length))
          .fill(0)
          .map(() => new Worker(new URL('../web-workers/image-processing.worker', import.meta.url)))
      );
    }
    this.#webWorkersTeamDoneStatus = new Array(teamSize).fill(false);
  }
}
